import { useState, useMemo, useCallback, useEffect } from "react";
import { Network, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MemoryGraphD3 } from "@/components/memory/MemoryGraphD3";
import { GraphInspector } from "@/components/memory/GraphInspector";
import { useMemoryMeta } from "@/hooks/useMemoryMeta";
import { useMemoryGraph } from "@/hooks/useMemoryGraph";
import { computeFreshness, type FreshnessState } from "@/lib/memoryUtils";
import { cn } from "@/lib/utils";

interface GraphHUDPanelProps {
  projectSlug: string;
  now?: number;
}

const FRESHNESS_BADGE: Record<FreshnessState, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  fresh: { label: "Fresh", variant: "default" },
  stale: { label: "Stale", variant: "secondary" },
  unknown: { label: "Unknown", variant: "outline" },
};

type ViewMode = "graph" | "list";

function NodeList({ nodes }: { nodes: Array<{ node_type: string }> }) {
  const groups = useMemo(() => {
    const map = new Map<string, number>();
    for (const n of nodes) {
      map.set(n.node_type, (map.get(n.node_type) ?? 0) + 1);
    }
    return Array.from(map.entries()).sort(([, a], [, b]) => b - a);
  }, [nodes]);

  if (groups.length === 0) {
    return (
      <div data-testid="graph-node-list" className="py-8 text-center text-sm text-muted-foreground">
        No nodes in graph.
      </div>
    );
  }

  return (
    <ul data-testid="graph-node-list" className="divide-y divide-border">
      {groups.map(([type, count]) => (
        <li key={type} className="flex items-center justify-between px-1 py-2 text-sm">
          <span className="capitalize text-muted-foreground">{type}</span>
          <span className="tabular-nums font-medium">{count}</span>
        </li>
      ))}
    </ul>
  );
}

export function GraphHUDPanel({ projectSlug, now }: GraphHUDPanelProps) {
  const { meta } = useMemoryMeta(projectSlug);
  const { graph, loading, error, expand } = useMemoryGraph(projectSlug);
  const [view, setView] = useState<ViewMode>("graph");
  // T1: node selection state
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  // R1: reset selection when project changes to avoid stale focus dimming
  useEffect(() => { setSelectedNodeId(null); }, [projectSlug]);

  const freshness = computeFreshness(meta?.generated_at, now);
  const badge = FRESHNESS_BADGE[freshness];

  const seededCount = graph?.nodes.length ?? 0;
  const totalCount = meta?.graph.node_count ?? graph?.total_nodes ?? 0;

  // T1: handle node click — toggle select/deselect, expand only on select (D7)
  const handleNodeClick = useCallback(
    (nodeId: string) => {
      if (nodeId === selectedNodeId) {
        // Toggle: deselect without calling expand (wasteful fetch on deselect)
        setSelectedNodeId(null);
        return;
      }
      setSelectedNodeId(nodeId);
      void expand(nodeId);
    },
    [selectedNodeId, expand],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant={badge.variant}>{badge.label}</Badge>
          {meta && (
            <span className="text-xs text-muted-foreground">
              {meta.graph.node_count} nodes · {meta.graph.edge_count} edges
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant={view === "graph" ? "default" : "ghost"}
            size="sm"
            onClick={() => setView("graph")}
            aria-label="Graph view"
          >
            <Network className="h-4 w-4" />
            <span className="ml-1 hidden sm:inline">Graph</span>
          </Button>
          <Button
            variant={view === "list" ? "default" : "ghost"}
            size="sm"
            onClick={() => setView("list")}
            aria-label="List view"
          >
            <List className="h-4 w-4" />
            <span className="ml-1 hidden sm:inline">List</span>
          </Button>
        </div>
      </div>

      <div className={cn("relative rounded-md bg-card", view === "graph" ? "h-[calc(100vh-220px)]" : "border")}>
        {loading ? (
          <div
            data-testid="graph-skeleton"
            className="h-full w-full animate-pulse rounded-md bg-muted/40"
          />
        ) : error ? (
          <div
            data-testid="graph-error"
            className="flex h-full items-center justify-center text-sm text-destructive gap-2 p-4"
          >
            <span>Failed to load graph.</span>
            <span className="text-muted-foreground text-xs">{(error as Error).message}</span>
          </div>
        ) : view === "graph" ? (
          // T2: flex-row layout — graph takes flex-1, inspector is a fixed-width sibling
          <div className="flex h-full">
            <div className="relative min-w-0 flex-1">
              <MemoryGraphD3
                graph={graph ?? { nodes: [], edges: [], total_nodes: 0, has_more: false }}
                onNodeClick={handleNodeClick}
                selectedNodeId={selectedNodeId}
                className="h-full w-full"
              />
              {/* T3: seed disclosure with has_more hint */}
              {totalCount > 0 && (
                <div
                  data-testid="graph-seed-disclosure"
                  className="absolute bottom-3 right-3 z-10 text-xs text-zinc-500"
                >
                  {seededCount} of {totalCount}
                  {graph?.has_more && (
                    <span data-testid="graph-has-more-hint">
                      {" · click nodes to explore more"}
                    </span>
                  )}
                </div>
              )}
            </div>
            {/* T2: GraphInspector side panel — first consumer */}
            {selectedNodeId !== null && (
              <GraphInspector
                node={graph?.nodes.find((n) => n.node_id === selectedNodeId) ?? null}
                edges={
                  graph?.edges.filter(
                    (e) => e.source === selectedNodeId || e.target === selectedNodeId,
                  ) ?? []
                }
                onClose={() => setSelectedNodeId(null)}
                onNavigate={handleNodeClick}
                className="h-full"
              />
            )}
          </div>
        ) : (
          <NodeList nodes={graph?.nodes ?? []} />
        )}
      </div>
    </div>
  );
}
